import Link from 'next/link'

import classNames from 'classnames'

import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import { Icon } from '~/components/atoms/Icon/Icon'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Lozenge, { LozengeVariant } from '~/components/atoms/Lozenge/Lozenge'
import SpecificImage from '~/components/atoms/SpecificImage/SpecificImage'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { toURL } from '~/utils/urlResolver'

import Badge from '../Badge/Badge'
import type { PartnerProps } from '../PartnerCard/PartnerCard'

import styles from './PartnerResultsCard.module.css'

export type PartnerCategories = {
  name: string
  slug: string
  summary?: string
  image?: ImageProps
  categoryOrder?: number
}

export interface PartnerResultsCardProps extends PartnerProps {
  portrait?: boolean
  theme?: 'dark' | 'light'
  noLazyLoad?: boolean
  bordered?: boolean
}

interface PartnerResultsCardDetailsProps extends PartnerProps {
  theme?: 'dark' | 'light'
  size?: 'small' | 'large'
}

export const PartnerResultsCardDetails = ({
  name,
  summary,
  category,
  town,
  type,
  city,
  theme = 'dark',
  size = 'small',
}: PartnerResultsCardDetailsProps) => {
  const isSponsored = type === 'sponsored'
  const isAccredited = type === 'accredited'
  const isTrusted = type === 'trusted'

  return (
    <div className={styles.baseContainer}>
      <div
        className={classNames(styles.details, size && styles[`${size}Details`])}
      >
        <div className={styles.detailsInner}>
          {isSponsored && size === 'large' && (
            <Text uppercase className={styles.largeSponsored}>
              Sponsored Partner
            </Text>
          )}
          <div className={styles.titleContainer}>
            <Text size={TextSizes.primaryTitleMed} className={styles.title}>
              <span
                className={classNames(
                  styles.titleText,
                  isSponsored && styles.titleAlignedMiddle
                )}
              >
                {name}
              </span>
              {type && size !== 'large' && (
                <div className={styles.classifications}>
                  {isSponsored && (
                    <Lozenge
                      title="sponsored"
                      variant={LozengeVariant.ghost}
                      size="small"
                      count={''}
                    />
                  )}
                  {isTrusted && <Badge type="trusted" isDark />}
                </div>
              )}
            </Text>
          </div>
          <div className={styles.location}>
            <div className={styles.locationIcon}>
              <Icon
                type={theme === 'light' ? 'GeoLocationLight' : 'GeoLocation'}
              />
            </div>
            <div className={styles.locationName}>
              {town && (
                <Text size={TextSizes.bodyMed} className={styles.town}>
                  {town}
                </Text>
              )}
              {town && city && <Text size={TextSizes.bodyMed}>{', '}</Text>}

              <Text size={TextSizes.bodyMed} className={styles.city}>
                {city}
              </Text>
            </div>
          </div>
          {category && category.length > 0 && (
            <div className={styles.categories}>
              {category.map(
                cat =>
                  cat?.name && (
                    <div
                      key={cat.name}
                      className={classNames(
                        styles[`${theme}Category`],
                        styles.category
                      )}
                    >
                      <Text
                        size={TextSizes.bodyMed}
                        uppercase
                        className={styles.categoryName}
                      >
                        {cat.name}
                      </Text>
                    </div>
                  )
              )}
            </div>
          )}
          <Text size={TextSizes.bodyMed} className={styles.summary}>
            {summary}
          </Text>

          <Button
            variant={ButtonVariants.capsUnderlineSml}
            button={{
              type: ButtonTypes.button,
            }}
            className={styles.ctaLink}
          >
            View details
          </Button>
        </div>
      </div>
    </div>
  )
}

export default function PartnerResultsCard(props: PartnerResultsCardProps) {
  const {
    name,
    image,
    portrait = true,
    theme = 'dark',
    noLazyLoad = true,
    url,
    bordered,
  } = props
  return (
    <>
      <div
        className={classNames(
          styles.base,
          !portrait && styles.baseLandscape,
          styles[`${theme}BaseTheme`],
          bordered && styles.bordered
        )}
      >
        <Link href={toURL(url)}>
          <div className={styles.container}>
            <div className={styles.imageContainer}>
              {props.type === 'accredited' && (
                <div className={styles.accredditedPartnerBadge}>
                  <Icon type="AccreditedPartner" />
                </div>
              )}
              <div className={styles.imageContainerInner}>
                {image && image.asset && (
                  <SpecificImage
                    asset={image.asset}
                    alt={`${name} - MyArtBroker`}
                    width={400}
                    noLazyLoad={noLazyLoad}
                  />
                )}
              </div>
            </div>
            <PartnerResultsCardDetails {...props} />
          </div>
        </Link>
      </div>
    </>
  )
}
